import React from 'react';
import './paymentModeSelector.css';

const money = (n) =>
  `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const MODE_LABEL = {
  wallet: 'Wallet',
  card: 'Card',
  invoice_later: 'Invoice later',
  auto_charge: 'Saved-card auto-charge',
};

/**
 * How the price difference on an ALREADY-PAID booking settles, shown like the
 * Add-Booking payment selector (2026-07-16).
 *
 * On a price INCREASE the admin CHOOSES the rail (card / wallet / invoice later),
 * exactly like Add-Booking. The chosen rail (`mode`) settles ONLY the difference —
 * the booking's stored "originally paid by" method (`bookingMode`) is history and
 * is never changed here. A DECREASE always refunds to the wallet and a no-change
 * settles nothing, so those stay a fixed read-only outcome (the rail choice does
 * not apply). When `disabled` (an in-flight saved-card completion charge) the
 * chooser is locked and the stored-method outcome is shown read-only, so an open
 * card charge is never redirected.
 *
 * Props: bookingMode (stored method), mode (chosen rail), onChange(rail),
 * diff (liveTotal − alreadyCharged), available (wallet), context (payment-context
 * for card details), disabled (has_open_auto_charge).
 */
export default function EditSettlementSummary({ bookingMode, mode, onChange, diff, available, disabled, settledVia, invoiceNumber }) {
  const stored = bookingMode || 'wallet';
  // What the header says money-wise. `settledVia` is what actually happened;
  // the stored mode is only an intention, and printing it over a booking a
  // payment link already settled read as "Paid by: Invoice later" on money that
  // was in the bank (2026-08-30).
  const paidByLabel = settledVia && settledVia.kind !== 'none'
    ? (settledVia.short || settledVia.label)
    : (MODE_LABEL[stored] || stored);
  const d = round2(diff);
  const avail = Number(available) || 0;
  const chosen = mode || stored;
  const isIncrease = d > 0.005;
  const interactive = isIncrease && !disabled;

  // Outcome ({ title, sub, tone, walletDelta? }) for a given settlement rail at
  // the current diff. Drives the interactive card (by the chosen rail) and the
  // read-only card (by the stored method).
  const outcomeFor = (m) => {
    if (d <= 0.005 && d >= -0.005) {
      return { title: 'No price change', sub: 'Nothing to charge or refund.', tone: 'neutral' };
    }
    if (d < 0) {
      // A decrease always refunds to the wallet — the rail choice does not apply.
      // Named as the action it is, and spelled out: pressing Update MOVES this
      // money. The old copy read like a status ("Refund to wallet") on a green
      // card, which is how an admin refunds again by hand afterwards.
      return {
        title: `Refund ${money(Math.abs(d))} to wallet`,
        sub: `Saving this booking credits the client's wallet with ${money(Math.abs(d))} automatically. `
           + 'Do not refund it again by any other route.',
        tone: 'refund',
        walletDelta: Math.abs(d),
      };
    }
    // Wallet only when it covers — otherwise it's blocked and the difference is
    // invoiced (owner 2026-07-21: price adjustments are gone).
    if (m === 'wallet' && avail >= d - 0.005) {
      return {
        title: 'Charge wallet now',
        sub: `${money(d)} is charged from the client's wallet.`,
        tone: 'charge',
        walletDelta: -d,
      };
    }
    if (m === 'wallet') {
      // Selected wallet but it can't cover — surface it as a block, never an
      // adjustment. The option itself is disabled in the rail below.
      return {
        title: 'Wallet can’t cover this',
        sub: `The wallet only has ${money(avail)} — ${money(round2(d - avail))} short. Choose “Invoice later”.`,
        tone: 'neutral',
      };
    }
    // invoice_later (and anything else) → billed on the invoice. An already-
    // invoiced booking's increase lands on THAT invoice's balance right away
    // (the edit regenerates it); otherwise it rides the next one.
    return {
      title: 'Invoice later',
      sub: invoiceNumber
        ? `${money(d)} is added to invoice ${invoiceNumber}'s balance — no charge now.`
        : `${money(d)} is added to the client's next invoice — no charge now.`,
      tone: 'neutral',
    };
  };

  const outcome = outcomeFor(interactive ? chosen : stored);
  const showWalletRows = outcome.walletDelta !== undefined;
  const balanceAfter = round2(avail + (outcome.walletDelta || 0));

  // Clickable rail option (interactive increase only) — mirrors PaymentModeSelector.
  const walletShort = avail < d - 0.005;
  const opt = (value, disabled, title, subtitle) => {
    const active = chosen === value;
    return (
      <button
        type="button"
        key={value}
        className={`pms__opt${active ? ' pms__opt--active' : ''}${disabled ? ' pms__opt--disabled' : ''}`}
        onClick={() => { if (!disabled && onChange) onChange(value); }}
        disabled={disabled}
        aria-pressed={active}
      >
        <span className="pms__optTitle">{title}</span>
        <span className="pms__optSub">{subtitle}</span>
      </button>
    );
  };

  return (
    <div className="pms">
      <div className="pms__label">
        {interactive ? 'Originally paid by' : 'Paid by'}: {paidByLabel}
        {settledVia?.is_guest && (
          <span className="ess__guestWarn">
            {' '}— guest money; a reduction cannot go to the client&rsquo;s wallet
          </span>
        )}
      </div>

      {interactive && (
        <div className="pms__row">
          {/* Only two rails now (2026-07-21): wallet when it covers, else
              invoice. No "Send Adjustment" — price adjustments are gone. Wallet
              is DISABLED when it can't cover, so a short wallet is never chosen. */}
          {opt(
            'wallet',
            walletShort,
            'Wallet',
            walletShort
              ? `${money(avail)} available — ${money(round2(d - avail))} short`
              : `${money(avail)} available — charged now`,
          )}
          {opt(
            'invoice_later',
            false,
            'Invoice later',
            'Added to the next invoice — no charge now',
          )}
        </div>
      )}


      <div className={`ess__card ess__card--${outcome.tone}`}>
        <span className="ess__title">{outcome.title}</span>
        <span className="ess__sub">{outcome.sub}</span>
      </div>

      {showWalletRows && (
        <div className="pms__wallet">
          <div className="pms__walletRow">
            <span>Available balance</span>
            <span className="pms__walletVal">{money(avail)}</span>
          </div>
          <div className="pms__walletRow">
            <span>{outcome.walletDelta > 0 ? 'Refund' : 'This change'}</span>
            <span className={`pms__walletVal${outcome.walletDelta < 0 ? ' pms__walletVal--neg' : ''}`}>
              {outcome.walletDelta > 0 ? '+' : '−'}{money(Math.abs(outcome.walletDelta))}
            </span>
          </div>
          <div className="pms__walletRow pms__walletRow--after">
            <span>Balance after</span>
            <span className="pms__walletVal">{money(balanceAfter)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
